import { useEffect, useState, useRef } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  Alert,
  Grid,
  Snackbar,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Stack,
  CircularProgress,
} from "@mui/material";
import PerspectiveContainer from "components/PerspectiveContainer";
import KpiNamesModel from "models/KpiNamesModel";
import { getKpiNames, addKpiName, deleteKpiName } from "services/api";
import LoadingScreen from "components/Loading";
import MDButton from "components/MDButton";
import { CloudDownload, CloudUpload } from "@mui/icons-material";
import { exportData } from "services/api";
import * as XLSX from "xlsx";
import { detectDataType } from "services/utils";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/authContext";
import { useTranslation } from "react-i18next";
import SnackbarWrapper from "components/Snackbar";
import PropTypes from "prop-types";
import ConfirmationDialog from "components/ConfirmationDialog";
import ImportButton from "components/ImportButton";

const perspectives = [
  "strategicInit",
  "strategicProjects",
  "financeViability",
  "internalProcess",
  "internalStakeholder",
  "externalStakeholder",
  "compliance",
  "audit",
];

const KPIPage = ({ canEdit }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState([]);
  const [deleteMode, setDeleteMode] = useState(false);
  const [toDeleteKpis, setToDeleteKpis] = useState([]);
  const [downloadLoading, setDownloadLoading] = useState(false);
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [formulaFilter, setFormulaFilter] = useState(""); // "" (All), "positive", "negative"
  const snackbarRef = useRef();
  const { companies } = useAuth();
  const [confOpenMulti, setConfOpenMulti] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const loadData = async () => {
    const data = await getKpiNames();
    if (data != "error") {
      const kpiData = data?.map((element) => new KpiNamesModel(element));
      setKpis(kpiData);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await loadData();
      } catch (error) {
        handleError(t("admin.inventory.failed_to_fetch_kpis"));
      }
    };

    fetchData().then((e) => {
      setLoading(false);
    });
  }, []);

  const handleError = (message) => {
    setErrorMessage(message);
    setErrorSnackbarOpen(true);
    setTimeout(() => {
      setErrorSnackbarOpen(false);
    }, 2000);
  };

  const handleSuccess = (message) => {
    // setSnackbarMessage(message);
    // setSnackbarOpen(true);
    snackbarRef.current?.showSnackbar(message, "success");

    setTimeout(() => {
      setErrorSnackbarOpen(false);
    }, 2000);
  };
  const moveKpi = async (kpiNameId, newPerspectiveId) => {
    try {
      const kpiToUpdate = kpis.find((kpi) => kpi.kpiNameId === kpiNameId);
      if (!kpiToUpdate) {
        return;
      }

      const updatedKpi = { ...kpiToUpdate, perspectiveId: newPerspectiveId };

      setKpis((prevKpis) =>
        prevKpis?.map((kpi) => (kpi.kpiNameId === kpiNameId ? updatedKpi : kpi))
      );

      const res = await addKpiName(updatedKpi);

      if (res === "error") {
        setKpis((prevKpis) =>
          prevKpis?.map((kpi) => (kpi.kpiNameId === kpiNameId ? kpiToUpdate : kpi))
        );
        handleError(t("admin.inventory.failed_to_update_kpi_backend"));
      }
    } catch (error) {
      handleError(t("admin.inventory.error_updating_kpi"));
    }
  };

  const addKpi = async (newKpi) => {
    try {
      setLoading(true);
      await loadData();
      handleSuccess(t("admin.inventory.kpi_saved_successfully"));
    } catch (error) {
      handleError(t("admin.inventory.error_adding_kpi"));
    }
    setLoading(false);
  };

  const removeKpi = async (kpiNameId) => {
    setLoading(true);
    const res = await deleteKpiName(kpiNameId);
    if (res !== "error") {
      await loadData();
    } else {
      handleError(t("admin.inventory.error_deleting_kpi"));
    }
    setLoading(false);
  };
  // Handle selecting/deselecting a KPI
  const handleSelectKpi = (kpiId) => {
    setToDeleteKpis((prev) =>
      prev.includes(kpiId) ? prev.filter((id) => id !== kpiId) : [...prev, kpiId]
    );
  };

  const handleDeleteSelectedKpis = async () => {
    if (toDeleteKpis.length === 0) {
      return;
    }

    setLoading(true);

    try {
      // Run all deletions in parallel
      await Promise.all(toDeleteKpis?.map((kpiId) => deleteKpiName(kpiId)));

      // Clear selected KPIs after successful deletion
      setToDeleteKpis([]);
      setDeleteMode(false);
      await loadData();

      handleSuccess(t("admin.inventory.selected_kpis_deleted"));
    } catch (error) {
      handleError(t("admin.inventory.error_deleting_selected_kpis"));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setDownloadLoading(true);
    await exportData("kpis");
    setDownloadLoading(false);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "application/vnd.ms-excel", // .xls
    ];

    if (!allowedTypes.includes(file.type)) {
      handleError(t("admin.inventory.invalid_file_type"));
      return;
    }

    try {
      const reader = new FileReader();
      reader.readAsBinaryString(file);

      reader.onload = (e) => {
        const binaryString = e.target.result;
        const workbook = XLSX.read(binaryString, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const data = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
          defval: "",
        });
        if (data.length === 0) {
          return;
        }

        const columns = Object.keys(data[0]);
        const dataType = detectDataType(columns);
        if (!dataType) {
          handleError(t("admin.inventory.file_data_not_recognized"));
          return;
        }

        if (dataType != "kpis") {
          handleError(t("admin.inventory.file_must_be_kpis_type"));
          return;
        }

        // 🎯 Filter only the allowed columns
        const allowedColumns = [
          "KPI ID",
          "KPI Name",
          "Perspective",
          "KPI Formula",
          "Description",
          "Theme",
          "Accountability",
          "Units",
          "Generated From",
          "Source",
        ];

        const cleanedData = data?.map((row) => {
          const cleanedRow = {};
          allowedColumns.forEach((col) => {
            cleanedRow[col] = row[col] ?? "";
          });
          return cleanedRow;
        });
        // Navigate to DataGrid page with parsed data
        navigate("/import", {
          state: {
            importedData: cleanedData,
            dataType,
            companyId: companies ? companies[0]?.companyId : null,
          },
        });
      };
    } catch (error) {
    } finally {
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  // Filtering logic
  const filteredKpis = kpis.filter((kpi) => {
    const matchesSearch = kpi.kpiName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFormula = formulaFilter === "" || kpi.kpiFormula.toLowerCase() === formulaFilter;
    return matchesSearch && matchesFormula;
  });

  const handleImportError = (message, severity) => {
    snackbarRef.current?.showSnackbar(message, severity);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          gap: 2,
          p: 2,
          flexWrap: "wrap",
        }}
      >
        {/* Left Section: Filters + Action buttons */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            flex: 1,
          }}
        >
          {/* Search */}
          <TextField
            label={t("admin.inventory.searchKpi")}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ height: 40, minWidth: 200 }}
          />

          {/* Formula Dropdown */}
          <FormControl size="small" sx={{ minWidth: 120, height: 40 }}>
            <InputLabel shrink sx={{ fontSize: 14 }}>
              {t("admin.inventory.formula")}
            </InputLabel>
            <Select
              value={formulaFilter}
              onChange={(e) => setFormulaFilter(e.target.value)}
              displayEmpty
              sx={{
                height: 40,
                fontSize: 14,
              }}
            >
              <MenuItem value="">{t("admin.inventory.all")}</MenuItem>
              <MenuItem value="regular">+</MenuItem>
              <MenuItem value="reverse">-</MenuItem>
            </Select>
          </FormControl>

          {/* Delete buttons */}
          {filteredKpis.length > 1 && (
            <MDButton
              variant="outlined"
              color={deleteMode ? "secondary" : "error"}
              onClick={() => setDeleteMode(!deleteMode)}
              sx={{ height: 40 }}
              disabled={!canEdit}
            >
              {deleteMode ? t("admin.inventory.cancel") : t("admin.inventory.deleteMultiple")}
            </MDButton>
          )}

          {deleteMode && toDeleteKpis.length > 0 && (
            <MDButton
              variant="contained"
              color="error"
              // onClick={handleDeleteSelectedKpis}
              onClick={() => setConfOpenMulti(true)}
              sx={{ height: 40 }}
              disabled={!canEdit}
            >
              {t("admin.inventory.confirmDelete")} ({toDeleteKpis.length})
            </MDButton>
          )}
        </Box>

        {/* Right Section: Import/Export buttons */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mt: { xs: 2, md: 0 }, justifyContent: "flex-end" }}
        >
          <MDButton
            color="primary"
            onClick={handleExport}
            startIcon={<CloudDownload />}
            disabled={downloadLoading}
          >
            {downloadLoading ? <CircularProgress size={20} color="inherit" /> : t("export")}
          </MDButton>

          <ImportButton
            // Only allow Jobs to be imported from this page
            allowedTypes={["kpis"]}
            // Pass context needed for the ImportReview page (optional now, mostly companyId)
            extraState={{ companyId: companies ? companies[0]?.companyId : null }}
            // Handle UI feedback
            onError={handleImportError}
            disabled={!canEdit}
          />
        </Stack>
      </Box>

      <Grid container spacing={2} sx={{ p: 3, flexDirection: { xs: "column", md: "row" } }}>
        {perspectives.map((perspective, index) => {
          const perspectiveId = index + 1;
          return (
            <Grid item xs={12} md={3} key={perspectiveId}>
              <PerspectiveContainer
                // title={perspective}
                title={t(`admin.perspectives.${perspective}`)}
                perspectiveId={perspectiveId}
                kpis={filteredKpis.filter((kpi) => kpi.perspectiveId === perspectiveId)}
                moveKpi={moveKpi}
                addKpi={addKpi}
                removeKpi={removeKpi}
                allKpis={kpis}
                deleteMode={deleteMode}
                toDeleteKpis={toDeleteKpis}
                handleSelectKpi={handleSelectKpi}
                canEdit={canEdit}
              />
            </Grid>
          );
        })}
      </Grid>

      <ConfirmationDialog
        open={confOpenMulti}
        onClose={() => setConfOpenMulti(false)}
        message={`Are you sure you want to delete ${toDeleteKpis.length} KPI(s)?`}
        onConfirm={async () => {
          setLoadingDelete(true);
          try {
            await handleDeleteSelectedKpis();
            setConfOpenMulti(false);
          } catch (err) {
            snackbarRef.current?.showSnackbar(t("admin.inventory.errorDeletingKpi"), "error");
          } finally {
            setLoadingDelete(false);
          }
        }}
      />

      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </DndProvider>
  );
};

KPIPage.propTypes = {
  canEdit: PropTypes.bool.isRequired,
};

export default KPIPage;
